import React, { FC } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faArrowsRotate } from "@fortawesome/pro-regular-svg-icons/faArrowsRotate";
import { faBriefcase } from "@fortawesome/pro-regular-svg-icons/faBriefcase";
import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { faHome } from "@fortawesome/pro-regular-svg-icons/faHome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { TripType } from "@mapmycustomers/shared";
import { Trip } from "@mapmycustomers/shared/types/entity";

import localSettings from "@app/config/LocalSettings";
import { meterDistanceFormatter } from "@app/util/fieldModel/impl/fieldUtil";
import { formatDuration } from "@app/util/formatters";

import styles from "./Subheader.module.scss";

const typeToIcon: Record<"unclassified" | TripType, IconDefinition> = {
  [TripType.BUSINESS]: faBriefcase,
  [TripType.PERSONAL]: faHome,
  unclassified: faInfoCircle,
};

export interface SubheaderProps {
  showAmounts: boolean;
  trip: Trip;
}

const Subheader: FC<SubheaderProps> = ({ showAmounts, trip }) => {
  const intl = useIntl();
  const currencyCode = localSettings.getCurrencyCode();

  return (
    <div className={styles.container}>
      <div className={styles.item}>
        <FontAwesomeIcon icon={typeToIcon[trip.type || "unclassified"]} />
        {meterDistanceFormatter({}, trip.distance)}
      </div>
      {showAmounts && (
        <div className={styles.item}>
          {intl.formatNumber(trip.type === TripType.BUSINESS ? trip.tripCost : 0, {
            currency: currencyCode,
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
          })}
        </div>
      )}
      <div className={styles.item}>
        <FontAwesomeIcon icon={faClock} />
        {formatDuration(intl, trip.duration)}
      </div>
      {trip.classification && (
        <div className={styles.item}>
          <FontAwesomeIcon icon={faArrowsRotate} />
          {intl.formatMessage(
            {
              id: "reports.mileageTracking.preview.details.type",
              defaultMessage:
                "Auto tracked{autoClassified, select, true { and classified} other {}}",
              description: "Trip type message in Trip preview pane",
            },
            {
              autoClassified: trip.classification === "auto tracked and classified",
            }
          )}
        </div>
      )}
    </div>
  );
};

export default Subheader;
